import React from 'react';
import {
  Image,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { NavigationProp, useRoute } from '@react-navigation/native';

import { ms, s, vs } from '../../utils/responsive';

import backIcon from '../../assets/icons/back.png';

interface Props {
  navigation: NavigationProp<any>;
}

/**
 * Step 1 of 3 in recovery-code enrollment.
 *
 * Plain-language explanation of what the code does + the unavoidable
 * tradeoff ("we can't recover it for you"). Users who read this and
 * bail are exactly the users who shouldn't enroll anyway; better to
 * lose them here than to have them enroll and then forget.
 */
const SetRecoveryCodeIntroScreen: React.FC<Props> = ({ navigation }) => {
  // Set when a document save sent the user here; carried to the success
  // screen, which returns there.
  const returnTo = (useRoute<any>().params as { returnTo?: unknown } | undefined)?.returnTo;
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Set Recovery Code</Text>
        <View style={{ width: ms(24) }} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.stepLabel}>Step 1 of 3</Text>
        <Text style={styles.title}>Before you set a recovery code</Text>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>What this code does</Text>
          <Text style={styles.cardBody}>
            It's what unlocks your vault on a new device. If you ever replace
            your phone, you'll enter this code to get your data back.
          </Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Until you set one</Text>
          <Text style={styles.cardBody}>
            Your documents and saved logins are protected by FinalKey's
            server, not by you. Setting a code moves that protection into
            your hands: from then on, not even FinalKey can read them.
          </Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>What it doesn't do</Text>
          <Text style={styles.cardBody}>
            It isn't your password. You'll still log in the usual way. The
            recovery code is a separate, second thing — only needed when you
            move to a new device or we need to prove it's really you.
          </Text>
        </View>

        <View style={[styles.card, styles.warningCard]}>
          <Text style={styles.warningTitle}>⚠ We cannot recover it for you</Text>
          <Text style={styles.warningBody}>
            Your vault is encrypted with a key derived from this code. If you
            forget it, we can't help — we don't have it either. Write it down
            somewhere safe before you set it.
          </Text>
          <Text style={[styles.warningBody, { marginTop: vs(8) }]}>
            Examples of "somewhere safe":
          </Text>
          <Text style={styles.bullet}>• A password manager (1Password, etc.)</Text>
          <Text style={styles.bullet}>• A piece of paper in a locked drawer or safe</Text>
          <Text style={styles.bullet}>• Shared with a successor you trust</Text>
        </View>

        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.navigate('SetRecoveryCodeInput', { returnTo })}
          accessibilityRole="button"
        >
          <Text style={styles.primaryButtonText}>I understand — continue</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.cancelButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
        >
          <Text style={styles.cancelButtonText}>Not right now</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: {
    padding: s(4),
  },
  backIcon: {
    width: ms(24),
    height: ms(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(16),
    fontWeight: '600',
    color: '#1A1A1A',
  },
  content: {
    padding: s(20),
  },
  stepLabel: {
    fontSize: ms(12),
    fontWeight: '600',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: vs(4),
  },
  title: {
    fontSize: ms(22),
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: vs(20),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: vs(14),
    borderWidth: 1,
    borderColor: '#E6E8EA',
  },
  cardTitle: {
    fontSize: ms(15),
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: vs(6),
  },
  cardBody: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
  },
  warningCard: {
    backgroundColor: '#FFF4E0',
    borderColor: '#E6A23C',
  },
  warningTitle: {
    fontSize: ms(15),
    fontWeight: '700',
    color: '#92400E',
    marginBottom: vs(6),
  },
  warningBody: {
    fontSize: ms(14),
    color: '#5A3F0F',
    lineHeight: ms(22),
  },
  bullet: {
    fontSize: ms(14),
    color: '#5A3F0F',
    lineHeight: ms(22),
    marginLeft: s(8),
    marginTop: vs(4),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    paddingHorizontal: s(40),
    borderRadius: ms(8),
    alignItems: 'center',
    marginTop: vs(12),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
  cancelButton: {
    paddingVertical: vs(12),
    alignItems: 'center',
    marginTop: vs(4),
  },
  cancelButtonText: {
    color: '#5A5A5A',
    fontSize: ms(14),
  },
});

export default SetRecoveryCodeIntroScreen;
